import { useSyncExternalStore } from 'react';

/*
 * Instalar o app na tela inicial.
 *
 * Chrome, Edge e Samsung Internet disparam `beforeinstallprompt` quando o site
 * pode ser instalado. Seguramos o evento (sem isso o navegador mostra a barra
 * dele, na hora que ele quer) e o usamos quando a pessoa tocar em "Instalar".
 * O evento chega cedo, às vezes antes do React montar: por isso o ouvinte é
 * registrado aqui, no carregamento do módulo, e não num efeito.
 *
 * Safari do iPhone não tem esse evento: lá só dá para explicar o caminho
 * (Compartilhar > Adicionar à Tela de Início).
 */

/** Evento do Chromium; não está no lib.dom. */
interface EventoInstalar extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let pendente: EventoInstalar | null = null;
let instalou = false;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((f) => f());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    pendente = e as EventoInstalar;
    avisar();
  });
  window.addEventListener('appinstalled', () => {
    pendente = null;
    instalou = true;
    avisar();
  });
}

/** Aberto da tela inicial (já instalado) em vez de uma aba do navegador. */
function abertoComoApp(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function ehIphone(): boolean {
  const ua = navigator.userAgent;
  // iPad com iPadOS 13+ se apresenta como Mac; o toque denuncia. No iOS todo
  // navegador põe na tela inicial pelo Compartilhar, então o texto vale para
  // Safari, Chrome e Firefox.
  return /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** `nativo`: dá para abrir o instalador. `iphone`: só explicando. `null`: nada a oferecer. */
export type ModoInstalar = 'nativo' | 'iphone' | null;

function ler(): ModoInstalar {
  if (typeof window === 'undefined' || instalou || abertoComoApp()) return null;
  if (pendente) return 'nativo';
  return ehIphone() ? 'iphone' : null;
}

function assinar(f: () => void): () => void {
  ouvintes.add(f);
  return () => ouvintes.delete(f);
}

export function useModoInstalar(): ModoInstalar {
  return useSyncExternalStore(assinar, ler, () => null);
}

/** Abre o instalador do navegador. Devolve se a pessoa aceitou. */
export async function abrirInstalador(): Promise<boolean> {
  const evento = pendente;
  if (!evento) return false;
  // O evento só serve uma vez; toque duplo faria o segundo prompt() lançar.
  pendente = null;
  try {
    await evento.prompt();
    const { outcome } = await evento.userChoice;
    return outcome === 'accepted';
  } catch {
    return false;
  } finally {
    avisar();
  }
}
